import { useDispatch, useSelector } from "react-redux";
import { DateFilters } from "../../../enums/DateFilters";
import { useCallback, useEffect, useState } from "react";
import { DateFilter as DateFilterInterface } from "../../../interfaces/DateFilter";
import { DateRange } from "react-day-picker";
import ClearIcon from "@mui/icons-material/Clear";
import DateHelper from "../../../helpers/date.helper";
import { setDate } from "../../../../store/filters/filters.slice";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import RangePicker from "../../range-picker/RangePicker";
import StringHelper from "../../../helpers/string.helper";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import classes from "./DateFilter.module.scss";
import { Content } from "../../../enums/Content";
import { RootState } from "../../../../store";
import { setTipsDate } from "../../../../store/filters/tipFilters.slice";

const dateFilters = Object.values(DateFilters);

const DateFilter = ({ content }: { content: Content }) => {
  const currentFilterDate = useSelector((state: RootState) =>
    content === Content.TIPS
      ? state.tipFilters.tipFilters.date
      : state.filters.filters.date
  );
  const [dateFilter, setDateFilter] = useState<DateFilterInterface | null>(
    currentFilterDate
  );
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const dispatch = useDispatch();

  const dispatchDateAction = useCallback(
    (payload: DateFilterInterface | null) => {
      if (content === Content.TIPS) {
        dispatch(setTipsDate(payload));
      } else {
        dispatch(setDate(payload));
      }
    },
    [dispatch, content]
  );

  const setRange = (range: DateRange) => {
    if (range.from && range.to) {
      const dateFilter: DateFilterInterface = {
        name: DateFilters.CUSTOM_DATE,
        start: DateHelper.getDateFormatted(range.from),
        end: DateHelper.getDateFormatted(range.to),
      };
      dispatchDateAction(dateFilter);
    }
  };

  useEffect(() => {
    setDateFilter(currentFilterDate);
  }, [currentFilterDate]);

  useEffect(() => {
    if (dateFilter && DateFilters.CUSTOM_DATE !== dateFilter.name) {
      dispatchDateAction(dateFilter);
    }
  }, [dateFilter, dispatchDateAction]);

  const dateButtonClickHandler = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const menuItemClickHandler = useCallback((date: DateFilters) => {
    if (date !== DateFilters.CUSTOM_DATE) {
      setDateFilter(DateHelper.getDateFilterByDateName(date));
    } else {
      setIsCalendarOpen(true);
    }
    setMenuAnchorEl(null);
  }, []);

  const clearMenuItemClickHandler = useCallback(() => {
    dispatchDateAction(null);
    setMenuAnchorEl(null);
  }, [dispatchDateAction]);

  return (
    <Box>
      <>
        <Box
          className={classes["date-button"]}
          alignContent="center"
          alignItems="center"
        >
          {content === Content.TIPS && (
            <Typography variant="caption" className="mx-3">
              {dateFilter?.name === DateFilters.CUSTOM_DATE
                ? `${dateFilter.start} - ${dateFilter.end}`
                : dateFilter?.name ?? "Select Date"}
            </Typography>
          )}
          <IconButton onClick={dateButtonClickHandler}>
            <CalendarMonthIcon />
          </IconButton>
        </Box>
        <Menu
          classes={{ list: classes.list }}
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={() => setMenuAnchorEl(null)}
        >
          {dateFilters.map((filter) => (
            <MenuItem
              className="my-1"
              selected={dateFilter?.name === filter}
              classes={{ selected: classes["selected-item"] }}
              key={StringHelper.generateRandomString(10)}
              onClick={() => menuItemClickHandler(filter)}
            >
              <Typography className="h6">{filter}</Typography>
            </MenuItem>
          ))}
          {dateFilter && (
            <MenuItem className="my-2" onClick={clearMenuItemClickHandler}>
              <Box className=" w-100 d-flex align-items-center justify-content-center">
                <ClearIcon fontSize="small" />
                <Typography className="h6">Clear</Typography>
              </Box>
            </MenuItem>
          )}
        </Menu>
      </>
      {isCalendarOpen && (
        <RangePicker
          isCalendarOpen={isCalendarOpen}
          closeCalendar={() => setIsCalendarOpen(false)}
          getRange={setRange}
          isModal={true}
        />
      )}
    </Box>
  );
};

export default DateFilter;
